import { useCallback, useEffect, useRef, useState } from 'react'
import { applyDrag, fitInside, outputSize, type Handle, type Rect } from './cropGeometry'
import { Banner, Button, Spinner } from '@shop/ui'

/**
 * قصُّ الصورة وتدويرُها قبل رفعها — <b>بلا مكتبة</b>.
 *
 * وللواجهة أربعُ اعتماديّاتٍ وقتَ كتابة هذا، وقاصُّ صورٍ جاهزٌ يجلب معه شجرةً
 * كاملةً لأجل مستطيلٍ يُسحب. فكُتب بـ<c>canvas</c> وأحداثِ المؤشّر، وهي تعمل
 * باللمس والفأرة معًا بلا فرعين.
 *
 * <b>وليس القصُّ زينة</b>: صورةُ هاتفٍ حديثةٍ أربعةُ آلاف بكسلٍ عرضًا وأربعةُ
 * ميجا، والمعروضُ منها مربّعٌ صغيرٌ في بطاقة. فالقصُّ والتصغيرُ هنا يُنزلانها
 * إلى عشراتِ الكيلوبايتات — ويوفّران قرصَ الخادم ونسخَه الاحتياطيّة وزمنَ
 * التحميل على شبكةِ المحلّ.
 */

/** أطولُ ضلعٍ في الناتج. أكبرُ من هذا لا يُرى في أيّ شاشةٍ هنا. */
const MAX_EDGE = 1600

/** جودةُ JPEG — فوقها تكبر الملفّاتُ بلا فرقٍ تراه العين. */
const QUALITY = 0.85

export function ImageCropper({
  file,
  onDone,
  onCancel,
}: {
  file: File
  onDone: (cropped: File) => void
  onCancel: () => void
}) {
  const [source, setSource] = useState<ImageBitmap | null>(null)
  const [failed, setFailed] = useState(false)
  const [rotation, setRotation] = useState(0)
  const [crop, setCrop] = useState<Rect | null>(null)
  const [busy, setBusy] = useState(false)

  const drag = useRef<{ handle: Handle; startX: number; startY: number; from: Rect } | null>(null)

  // ⚠ وعرضُ الصندوق **حالةٌ** لا قراءةُ مرجعٍ أثناء الرسم: المرجعُ فارغٌ في أوّل
  //   رسمة، وقراءتُه لا تُعيد الرسمَ حين يمتلئ — فتبقى المعاينةُ على القيمة
  //   الاحتياطيّة أبدًا مهما اتّسعت الشاشة. والمراقبُ يتابع تغيّرَ الاتّجاه أيضًا.
  const [boxWidth, setBoxWidth] = useState(320)

  const measure = useCallback((node: HTMLDivElement | null) => {
    if (node === null) {
      return
    }

    setBoxWidth(node.clientWidth)

    const observer = new ResizeObserver(([entry]) => setBoxWidth(entry.contentRect.width))
    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  // ⚠ و`imageOrientation: 'from-image'` صريحةٌ لا افتراض: صورةُ الهاتف تحمل
  //   اتّجاهًا في EXIF، وبلا احترامه تُرسم مقلوبةً على جنبها في `canvas` —
  //   بينما تبدو معتدلةً في `<img>`. فيقصّ الموظّفُ ما يراه ويُحفظ غيرُه.
  useEffect(() => {
    let live = true
    let made: ImageBitmap | null = null

    createImageBitmap(file, { imageOrientation: 'from-image' })
      .then((bitmap) => {
        if (!live) {
          bitmap.close()

          return
        }

        made = bitmap
        setSource(bitmap)
      })
      .catch(() => {
        if (live) {
          setFailed(true)
        }
      })

    return () => {
      live = false
      made?.close()
    }
  }, [file])

  // أبعادُ الصورة بعد التدوير.
  const turned = rotation % 180 !== 0
  const sw = source === null ? 0 : (turned ? source.height : source.width)
  const sh = source === null ? 0 : (turned ? source.width : source.height)

  // المعروضُ داخل الصندوق بنسبةٍ محفوظة.
  const view = fit(sw, sh)
  const area = crop ?? { x: 0, y: 0, w: view.w, h: view.h }

  function fit(w: number, h: number) {
    return fitInside(w, h, boxWidth, 320)
  }

  const start = (handle: Handle) => (event: React.PointerEvent) => {
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.setPointerCapture(event.pointerId)

    drag.current = { handle, startX: event.clientX, startY: event.clientY, from: area }
  }

  const move = (event: React.PointerEvent) => {
    const active = drag.current

    if (active === null) {
      return
    }

    const dx = event.clientX - active.startX
    const dy = event.clientY - active.startY

    setCrop(applyDrag(active.handle, active.from, dx, dy, view.w, view.h))
  }

  const end = () => {
    drag.current = null
  }

  const use = async (whole: boolean) => {
    if (source === null) {
      return
    }

    setBusy(true)

    try {
      const region = whole
        ? { x: 0, y: 0, w: view.w, h: view.h }
        : area

      const blob = await render(source, rotation, region, view.scale)

      if (blob === null) {
        setFailed(true)

        return
      }

      // الاسمُ يُبقي أصلَه ليُعرف في السجلّات، واللاحقةُ تتبع الناتجَ لا المصدر.
      const name = file.name.replace(/\.[^.]+$/, '') || 'image'

      onDone(new File([blob], `${name}.jpg`, { type: 'image/jpeg' }))
    } finally {
      setBusy(false)
    }
  }

  if (failed) {
    return (
      <div className="space-y-2">
        <Banner tone="danger">تعذّر فتحُ الصورة. جرّب صورةً أخرى.</Banner>
        <Button onClick={onCancel}>إغلاق</Button>
      </div>
    )
  }

  if (source === null) {
    return <Spinner label="تُفتح الصورة…" />
  }

  return (
    <div className="space-y-3 rounded-md border border-line bg-surface p-3">
      <div ref={measure} className="flex justify-center">
        <div
          className="relative touch-none select-none"
          style={{ width: view.w, height: view.h }}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
        >
          <Canvas source={source} rotation={rotation} width={view.w} height={view.h} />

          {/* ما خارج المستطيل يُعتم، فيُرى المقصوصُ بلا حدودٍ تُخمَّن. */}
          <div className="pointer-events-none absolute inset-0 bg-ink/50" />
          <div
            className="pointer-events-none absolute overflow-hidden"
            style={{ left: area.x, top: area.y, width: area.w, height: area.h }}
          >
            <div
              className="absolute"
              style={{ left: -area.x, top: -area.y, width: view.w, height: view.h }}
            >
              <Canvas source={source} rotation={rotation} width={view.w} height={view.h} />
            </div>
          </div>

          <div
            className="absolute cursor-move border-2 border-brand"
            style={{ left: area.x, top: area.y, width: area.w, height: area.h }}
            onPointerDown={start('move')}
          >
            {(['nw', 'ne', 'sw', 'se'] as const).map((corner) => (
              <span
                key={corner}
                onPointerDown={start(corner)}
                className="absolute h-5 w-5 rounded-full border-2 border-surface bg-brand"
                style={cornerStyle(corner)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          disabled={busy}
          onClick={() => {
            setRotation((current) => (current + 90) % 360)
            setCrop(null)
          }}
          title="صورةُ الهاتف كثيرًا ما تصل على جنبها"
        >
          دوّر ٩٠°
        </Button>

        <Button disabled={busy} onClick={() => setCrop(null)}>أعِد المستطيل</Button>

        <Button tone="primary" disabled={busy} onClick={() => void use(false)}>
          {busy ? 'يُعالَج…' : 'اقصص واستعمل'}
        </Button>

        <Button disabled={busy} onClick={() => void use(true)}>الصورةُ كاملةً</Button>
        <Button disabled={busy} onClick={onCancel}>إلغاء</Button>
      </div>

      <p className="text-xs text-ink-soft">
        اسحب المستطيلَ أو أركانَه. وتُصغَّر الصورةُ إلى {MAX_EDGE} بكسلٍ على الأكثر
        وتُحفظ JPEG — فلا تُرفع أربعةُ ميجا لمربّعٍ في بطاقة.
      </p>
    </div>
  )
}

/** لوحةٌ ترسم الصورةَ بالتدوير المطلوب — وتُعاد الرسمُ عند كلّ تغيير. */
function Canvas({
  source,
  rotation,
  width,
  height,
}: {
  source: ImageBitmap
  rotation: number
  width: number
  height: number
}) {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = ref.current
    const context = canvas?.getContext('2d')

    if (!canvas || !context) {
      return
    }

    canvas.width = width
    canvas.height = height
    context.clearRect(0, 0, width, height)
    draw(context, source, rotation, width, height)
  }, [source, rotation, width, height])

  return <canvas ref={ref} className="block" style={{ width, height }} />
}

/**
 * يرسم الصورةَ مدوَّرةً لتملأ المساحةَ المعطاة.
 *
 * ⚠ والترتيبُ `translate` ثمّ `rotate` ثمّ الرسمُ من السالب: `rotate` وحدَها
 *   تدور حول الأصل لا حول المركز، فتخرج الصورةُ من اللوحة كلِّها.
 */
function draw(
  context: CanvasRenderingContext2D,
  source: ImageBitmap,
  rotation: number,
  width: number,
  height: number,
) {
  context.save()
  context.translate(width / 2, height / 2)
  context.rotate((rotation * Math.PI) / 180)

  const turned = rotation % 180 !== 0
  const w = turned ? height : width
  const h = turned ? width : height

  context.drawImage(source, -w / 2, -h / 2, w, h)
  context.restore()
}

/** يُخرج المقصوصَ ملفًّا — بأبعادِ المصدر لا بأبعاد العرض، ومُصغَّرًا إن كبر. */
async function render(
  source: ImageBitmap,
  rotation: number,
  region: Rect,
  scale: number,
): Promise<Blob | null> {
  const { fullW, fullH, outW, outH } = outputSize(region.w, region.h, scale, MAX_EDGE)

  const turned = rotation % 180 !== 0
  const wholeW = turned ? source.height : source.width
  const wholeH = turned ? source.width : source.height

  // تُرسم الصورةُ كاملةً مدوَّرةً أوّلًا ثمّ يُؤخذ منها المستطيل: خطوتان
  // تُقرآن، بدل مصفوفةِ تحويلٍ واحدةٍ تجمع الدورانَ والقصَّ والتصغير ولا
  // يُراجعها أحد.
  const whole = surface(wholeW, wholeH)
  const wholeContext = whole.getContext('2d') as CanvasRenderingContext2D | null

  if (wholeContext === null) {
    return null
  }

  draw(wholeContext, source, rotation, wholeW, wholeH)

  const out = surface(outW, outH)
  const outContext = out.getContext('2d') as CanvasRenderingContext2D | null

  if (outContext === null) {
    return null
  }

  outContext.imageSmoothingQuality = 'high'
  outContext.drawImage(
    whole as CanvasImageSource,
    region.x / scale, region.y / scale, fullW, fullH,
    0, 0, outW, outH,
  )

  return await toBlob(out)
}

/**
 * لوحةٌ خارج الشاشة — <c>OffscreenCanvas</c> إن وُجدت، وإلا عنصرٌ لا يُضاف للصفحة.
 *
 * ⚠ والبديلُ ليس احتياطًا نظريًّا: متصفّحُ جهازٍ أقدمَ بقليلٍ لا يعرفها، وبلا
 *   هذا يسقط القصُّ كلُّه بـ«OffscreenCanvas is not defined» — على جهازٍ واحدٍ
 *   في المحلّ، ويعمل على البقيّة، فيبدو عطبًا لا يُصدَّق.
 */
function surface(width: number, height: number): OffscreenCanvas | HTMLCanvasElement {
  if (typeof OffscreenCanvas === 'function') {
    return new OffscreenCanvas(width, height)
  }

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  return canvas
}

function toBlob(canvas: OffscreenCanvas | HTMLCanvasElement): Promise<Blob | null> {
  if (canvas instanceof HTMLCanvasElement) {
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY))
  }

  return canvas.convertToBlob({ type: 'image/jpeg', quality: QUALITY })
}

function cornerStyle(corner: Handle): React.CSSProperties {
  const common: React.CSSProperties = { touchAction: 'none' }

  switch (corner) {
    case 'nw':
      return { ...common, left: -10, top: -10, cursor: 'nwse-resize' }
    case 'ne':
      return { ...common, right: -10, top: -10, cursor: 'nesw-resize' }
    case 'sw':
      return { ...common, left: -10, bottom: -10, cursor: 'nesw-resize' }
    default:
      return { ...common, right: -10, bottom: -10, cursor: 'nwse-resize' }
  }
}
